import React, { useEffect, useRef, useState } from 'react';
import Button from '../Button';
import Input from '../Input';
import { Plus, Trash } from 'src/common/icons';
import styles from './styles.module.scss';

const DEFAULT_MAX_URLS = 5;

const isValidWebhookUrl = (url: string) =>
  url.startsWith('http://') || url.startsWith('https://');

// The API stores webhook URLs as a single comma-separated string; this
// component presents them as an editable list and converts on the way in/out.
const splitUrls = (value: string): string[] => {
  const urls = (value || '')
    .split(',')
    .map((url) => url.trim())
    .filter(Boolean);
  return urls.length > 0 ? urls : [''];
};

const joinUrls = (rows: string[]): string =>
  rows
    .map((url) => url.trim())
    .filter(Boolean)
    .join(',');

interface WebhookUrlListProps {
  value: string;
  onChange: (value: string) => void;
  maxUrls?: number;
}

export const WebhookUrlList: React.FC<WebhookUrlListProps> = ({
  value,
  onChange,
  maxUrls = DEFAULT_MAX_URLS,
}) => {
  const [rows, setRows] = useState<string[]>(() => splitUrls(value));
  // Tracks the last value we emitted so external resets (e.g. discard changes)
  // re-seed the rows, while our own onChange round-trips don't collapse
  // empty rows the user just added.
  const lastEmitted = useRef<string>(value || '');

  useEffect(() => {
    if ((value || '') !== lastEmitted.current) {
      lastEmitted.current = value || '';
      setRows(splitUrls(value));
    }
  }, [value]);

  const emit = (nextRows: string[]) => {
    setRows(nextRows);
    const joined = joinUrls(nextRows);
    lastEmitted.current = joined;
    onChange(joined);
  };

  const handleRowChange = (index: number, url: string) => {
    const nextRows = [...rows];
    nextRows[index] = url;
    emit(nextRows);
  };

  const handleRemove = (index: number) => {
    const nextRows = rows.filter((_, i) => i !== index);
    emit(nextRows.length > 0 ? nextRows : ['']);
  };

  const handleAdd = () => {
    if (rows.length < maxUrls) {
      setRows([...rows, '']);
    }
  };

  return (
    <div className={styles.webhookUrlList}>
      {rows.map((url, index) => {
        const trimmed = url.trim();
        const showError = trimmed !== '' && !isValidWebhookUrl(trimmed);
        return (
          <div key={index} className={styles.urlRow}>
            <div className={styles.urlInput}>
              <Input
                variant="secondary"
                value={url}
                onChange={(newUrl: string) => handleRowChange(index, newUrl)}
                placeholder="https://example.org/filespin_callback"
                error={showError ? 'Webhook URL must start with "http://" or "https://"' : undefined}
              />
            </div>
            {(rows.length > 1 || trimmed !== '') && (
              <Button
                mode="flat"
                size="small"
                className={styles.removeButton}
                icon={<Trash />}
                tooltip="Remove URL"
                aria-label="Remove URL"
                onClick={() => handleRemove(index)}
              />
            )}
          </div>
        );
      })}

      {rows.length < maxUrls && (
        <Button
          mode="secondary"
          size="small"
          outlined
          className={styles.addButton}
          icon={<Plus />}
          onClick={handleAdd}
        >
          Add URL
        </Button>
      )}

      <p className={styles.helperText}>
        Webhook URL must start with "http://" or "https://". Up to {maxUrls} webhook URLs can be added.
      </p>
    </div>
  );
};

export default WebhookUrlList;
